import type { Node } from "hast";

interface CustomNode extends Node {
  type: string;
  tagName?: string;
  value?: string;
  children?: CustomNode[];
  properties?: { [key: string]: any };
}

/**
 * rehype plugin to process <storage-key> elements emitted by the Time
 * Series toolkit's post-processor (see mre/utils/chat.py
 * process_storage_key_refs).
 *
 * The backend rewrites the model's
 *   <storage-key source="N">KEY</storage-key>
 * into
 *   <storage-key>{"source_id":N,...,"storage_key":"...",...}</storage-key>
 * after validating the (source_id, storage_key) pair against
 * sensor_metadata. Unresolvable refs are stripped server-side, so any
 * tag that reaches the frontend with a JSON body is real.
 *
 * Hyphen (not underscore) is load-bearing: CommonMark only recognises
 * inline HTML tag names matching ``[A-Za-z][A-Za-z0-9-]*``. With an
 * underscore, remark drops the tag through as literal text, rehype-raw
 * never sees it as HTML, and this plugin never gets a chance to run.
 *
 * This plugin parses that JSON, attaches it as `payload` on the element's
 * properties, and lets <StorageKeyLink> render the chip downstream.
 */
export function rehypeWrapStorageKey() {
  return (tree: CustomNode) => {
    function visit(node: CustomNode) {
      if (!node.children) return;

      for (let i = 0; i < node.children.length; i++) {
        const child = node.children[i];

        if (child.type === "element" && child.tagName === "storage-key") {
          const text = extractTextContent(child).trim();
          processStorageKeyNode(child, text);
        }

        visit(child);
      }
    }

    visit(tree);
  };
}

function processStorageKeyNode(node: CustomNode, text: string) {
  let payload: Record<string, any> | null = null;

  // Body should be a JSON object — anything else means the backend
  // resolver stripped or never touched this tag. Render the raw text as
  // a fallback chip rather than swallowing it silently.
  const startIdx = text.indexOf("{");
  const endIdx = text.lastIndexOf("}");
  if (startIdx !== -1 && endIdx !== -1 && endIdx > startIdx) {
    try {
      const candidate = JSON.parse(text.slice(startIdx, endIdx + 1));
      if (candidate && typeof candidate === "object") {
        payload = candidate;
      }
    } catch {
      // Malformed JSON — leave payload null; StorageKeyLink falls back.
    }
  }

  if (!node.properties) {
    node.properties = {};
  }
  node.properties.payload = payload;
  // Also expose the raw text body for the fallback rendering path so
  // the component doesn't need to extract it again.
  node.properties.rawBody = text;
}

function extractTextContent(node: CustomNode): string {
  if (node.type === "text") {
    let text = node.value || "";
    // Mirror the unescaping in rehypeWrapCite — AAAssistantText escapes
    // markdown-special chars inside <storage-key>...</storage-key> bodies
    // before passing content to react-markdown, and we have to reverse
    // it here to recover valid JSON.
    text = text.replace(/\\\\"/g, '\\"');
    text = text.replace(/\\_/g, "_");
    text = text.replace(/\\\*/g, "*");
    text = text.replace(/\\`/g, "`");
    return text;
  }

  if (node.children) {
    return node.children.map(extractTextContent).join("");
  }

  return "";
}
